import { useState, useEffect, useCallback } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  FileCheck, RefreshCw, Download, Printer, AlertTriangle, ChevronLeft, ChevronRight,
  FilePlus, FileText, X, ReceiptText, Clock, TrendingDown, Info, Accessibility
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/dateFormat';
import InvoiceDetailModal from '../components/InvoiceDetailModal';

const VAT_TYPE_LABELS = { vat_registered: 'VAT Registered (12%)', non_vat: 'Non-VAT' };

function SummaryCard({ label, value, sub, color = '' }) {
  return (
    <div className="rounded-xl border p-4 bg-white" style={{ borderColor: 'var(--border-default)' }}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{label}</p>
      <p className={`text-xl font-bold ${color || 'text-slate-800'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Generate BIRZ Dialog ──────────────────────────────────────────────────────
function GenerateBIRZDialog({ open, onClose, branchId, onGenerated }) {
  const today = new Date().toISOString().split('T')[0];
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [periodLabel, setPeriodLabel] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!fromDate || !toDate) { toast.error('Select a date range'); return; }
    if (fromDate > toDate) { toast.error('From date must be before To date'); return; }
    setLoading(true);
    try {
      const res = await api.post('/bir/z-report/generate', {
        branch_id: branchId, from_date: fromDate, to_date: toDate,
        period_label: periodLabel || undefined,
      }, { responseType: 'blob' });
      const birzNum = res.headers['x-birz-number'] || 'BIRZ Report';
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `BIR-ZReport-${fromDate}-to-${toDate}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${birzNum} generated and downloaded`);
      onGenerated(); onClose();
    } catch (e) {
      const msg = e.response?.data ? await e.response.data.text?.() : '';
      let detail = ''; try { detail = JSON.parse(msg)?.detail || ''; } catch {}
      toast.error(detail || 'Failed to generate BIR Z-Report');
    }
    setLoading(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="birz-generate-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-[440px] p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-[16px] text-slate-900">Generate BIR Z-Report</h3>
            <p className="text-xs text-slate-500 mt-0.5">Creates an official BIR Sales Report with a sequential BIRZ number</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs text-amber-800 font-semibold">Important</p>
          <p className="text-xs text-amber-700 mt-0.5">Each period can only have one BIR Z-Report. Once generated, the BIRZ number cannot be reused.</p>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Period Label (optional)</label>
            <Input placeholder="e.g. June 2026" value={periodLabel} onChange={e => setPeriodLabel(e.target.value)} className="mt-1 h-9 text-sm" data-testid="birz-period-label" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">From Date</label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="mt-1 h-9 text-sm" data-testid="birz-from-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">To Date</label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="mt-1 h-9 text-sm" data-testid="birz-to-date" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button className="flex-1 bg-[#1A4D2E] hover:bg-[#14532d] text-white" onClick={handleGenerate} disabled={loading} data-testid="birz-generate-confirm-btn">
            {loading ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <FilePlus size={13} className="mr-1.5" />}
            Generate & Download
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Aging Bucket Badge ────────────────────────────────────────────────────────
function AgingBadge({ bucket }) {
  const map = {
    '0-30 days': 'bg-green-100 text-green-700',
    '31-60 days': 'bg-yellow-100 text-yellow-700',
    '61-90 days': 'bg-orange-100 text-orange-700',
    'Over 90 days': 'bg-red-100 text-red-700',
  };
  return <Badge className={`text-[10px] ${map[bucket] || 'bg-slate-100 text-slate-600'}`}>{bucket}</Badge>;
}

// ── Tab IDs ────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'taxable',     label: 'Taxable Sales',     icon: ReceiptText, desc: 'SI + CI — principal taxable documents' },
  { id: 'ar_aging',   label: 'BIR AR Aging',       icon: Clock,       desc: 'Unpaid Charge Invoices — bad debt tracking' },
  { id: 'scpwd',      label: 'SC/PWD Log',         icon: Accessibility, desc: 'Senior Citizen / PWD discount logbook (RR 7-2010)' },
  { id: 'zreports',   label: 'Z-Reports',          icon: FileText,    desc: 'Past BIR Z-Reports' },
  { id: 'vat_rules',  label: 'Product VAT Rules',  icon: TrendingDown, desc: 'Category VAT rules + override report' },
];

export default function BIRCompliancePage() {
  const { currentBranch, user, branches } = useAuth();
  const [activeTab, setActiveTab] = useState('taxable');

  // Taxable Sales state
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);

  // AR Aging state
  const [agingList, setAgingList] = useState([]);
  const [agingSummary, setAgingSummary] = useState(null);

  // SC/PWD logbook state
  const [scpwdRows, setScpwdRows] = useState([]);
  const [scpwdSummary, setScpwdSummary] = useState(null);
  const [scpwdLoading, setScpwdLoading] = useState(false);

  // Common
  const [birStatus, setBirStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [detailInvoiceId, setDetailInvoiceId] = useState(null);

  // Filters
  const today = new Date().toISOString().split('T')[0];
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [includeVoided, setIncludeVoided] = useState(true);
  const [quarterSel, setQuarterSel] = useState('');

  // BIR 2550Q quarter presets — current year + previous 2 years
  const quarterOptions = (() => {
    const yr = new Date().getFullYear();
    const defs = [
      ['Q1', 'Jan–Mar', '01-01', '03-31'],
      ['Q2', 'Apr–Jun', '04-01', '06-30'],
      ['Q3', 'Jul–Sep', '07-01', '09-30'],
      ['Q4', 'Oct–Dec', '10-01', '12-31'],
    ];
    const out = [];
    for (const y of [yr, yr - 1, yr - 2]) {
      for (const [q, mon, fs, ts] of defs) {
        out.push({ value: `${y}-${q}`, label: `${q} ${y} (${mon})`, from: `${y}-${fs}`, to: `${y}-${ts}` });
      }
    }
    return out;
  })();

  const applyQuarter = (val) => {
    setQuarterSel(val);
    const opt = quarterOptions.find(o => o.value === val);
    if (opt) { setFromDate(opt.from); setToDate(opt.to); }
  };

  // Pagination
  const [page, setPage] = useState(0);
  const LIMIT = 100;

  // BIRZ reports
  const [birzReports, setBirzReports] = useState([]);
  const [birzLoading, setBirzLoading] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  const effectiveBranch = selectedBranch || currentBranch?.id || '';

  const loadTaxableData = useCallback(async () => {
    if (!effectiveBranch) return;
    try {
      const [statusRes, invoicesRes] = await Promise.all([
        api.get('/bir/activation/status', { params: { branch_id: effectiveBranch } }),
        api.get('/bir/invoices', {
          params: { branch_id: effectiveBranch, from_date: fromDate, to_date: toDate, include_voided: includeVoided, doc_class: 'principal', limit: 2000 }
        }),
      ]);
      setBirStatus(statusRes.data);
      setInvoices(invoicesRes.data.invoices || []);
      setSummary(invoicesRes.data.summary || null);
      setPage(0);
    } catch { toast.error('Failed to load BIR data'); }
  }, [effectiveBranch, fromDate, toDate, includeVoided]);

  const loadArAging = useCallback(async () => {
    if (!effectiveBranch) return;
    try {
      const res = await api.get('/bir/ar-aging', { params: { branch_id: effectiveBranch } });
      setAgingList(res.data.aging_list || []);
      setAgingSummary(res.data.summary || null);
    } catch { toast.error('Failed to load AR aging'); }
  }, [effectiveBranch]);

  // VAT Rules state
  const [vatRules, setVatRules] = useState([]);
  const [vatOverrideReport, setVatOverrideReport] = useState({ overrides: [], conflicts: [], locked: [], total: 0 });
  const [allCategories, setAllCategories] = useState([]);
  const [vatRulesLoading, setVatRulesLoading] = useState(false);
  const [applyingRule, setApplyingRule] = useState('');
  const [applyResult, setApplyResult] = useState(null);

  const loadVatRules = useCallback(async () => {
    setVatRulesLoading(true);
    try {
      const [rulesRes, reportRes, catsRes] = await Promise.all([
        api.get('/bir/vat-category-rules'),
        api.get('/bir/vat-override-report'),
        api.get('/products/categories'),
      ]);
      setVatRules(rulesRes.data.rules || []);
      setVatOverrideReport(reportRes.data);
      setAllCategories(catsRes.data.categories || catsRes.data || []);
    } catch { toast.error('Failed to load VAT rules'); }
    setVatRulesLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'vat_rules') loadVatRules();
  }, [activeTab, loadVatRules]);

  const loadScpwd = useCallback(async () => {
    if (!effectiveBranch) return;
    setScpwdLoading(true);
    try {
      const res = await api.get('/bir/scpwd-log', { params: { branch_id: effectiveBranch, from_date: fromDate, to_date: toDate } });
      setScpwdRows(res.data.rows || []);
      setScpwdSummary(res.data.summary || null);
    } catch { toast.error('Failed to load SC/PWD log'); }
    setScpwdLoading(false);
  }, [effectiveBranch, fromDate, toDate]);

  useEffect(() => {
    if (activeTab === 'scpwd') loadScpwd();
  }, [activeTab, loadScpwd]);

  const loadBirzReports = useCallback(async () => {
    if (!effectiveBranch) return;
    setBirzLoading(true);
    try {
      const res = await api.get('/bir/z-report/list', { params: { branch_id: effectiveBranch, limit: 20 } });
      setBirzReports(res.data.reports || []);
    } catch { /* non-critical */ }
    setBirzLoading(false);
  }, [effectiveBranch]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadTaxableData(), loadArAging(), loadBirzReports()]);
    setLoading(false);
  }, [loadTaxableData, loadArAging, loadBirzReports]);

  useEffect(() => {
    if (effectiveBranch) loadAll();
  }, [loadAll, effectiveBranch]);

  useEffect(() => {
    setSelectedBranch(currentBranch?.id || '');
  }, [currentBranch?.id]);

  const handleDownloadBirz = async (report) => {
    try {
      const res = await api.get(`/bir/z-report/${report.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `${report.birz_number}-${report.from_date}-to-${report.to_date}.pdf`; a.click();
      URL.revokeObjectURL(url); toast.success('Downloaded');
    } catch { toast.error('Failed to download report'); }
  };

  const exportTaxableCSV = () => {
    if (!invoices.length) { toast.error('No data to export'); return; }
    const fmt = (n) => parseFloat(n || 0).toFixed(2);
    const q = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
    const rows = [];
    // Header block
    rows.push([q('BIR QUARTERLY SUMMARY LIST OF SALES (SLSP)')]);
    rows.push([q(`Period: ${fromDate} to ${toDate}`)]);
    rows.push([q(`Branch: ${branchName}`), q(`TIN: ${birStatus?.tin || ''}`)]);
    rows.push([q(`Export Date: ${new Date().toISOString().split('T')[0]}`)]);
    rows.push(['']);
    // Section A — Sales Invoices
    rows.push([q('SECTION A — SALES INVOICES (SI) | Principal Taxable Document — Cash / Digital Sales')]);
    rows.push(['SI #','OS Ref','Date Issued','Customer Name','Buyer TIN','Vatable Sales','Zero-Rated','Exempt','Output VAT (12%)','Gross Total','Status'].map(q));
    siInvoices.forEach(inv => rows.push([
      inv.bir_invoice_number, inv.invoice_number,
      inv.bir_issued_at ? inv.bir_issued_at.split('T')[0] : '',
      inv.customer_name || 'Walk-in', inv.bir_customer_tin || '',
      fmt(inv.vatable_sales), fmt(inv.zero_rated_sales), fmt(inv.vat_exempt_sales),
      fmt(inv.vat_amount), fmt(inv.grand_total),
      inv.status === 'voided' ? 'VOID' : 'ISSUED',
    ].map(q)));
    rows.push(['','','','','SI SUBTOTAL', fmt(siVatable), fmt(siZeroRated), fmt(siExempt), fmt(siVat), fmt(siTotal), ''].map(q));
    rows.push(['']);
    // Section B — Charge Invoices
    rows.push([q('SECTION B — CHARGE INVOICES (CI) | Principal Taxable Document — Credit / Charge Sales')]);
    rows.push(['CI #','OS Ref','Date Issued','Customer Name','Buyer TIN','Vatable Sales','Zero-Rated','Exempt','Output VAT (12%)','Gross Total','Supplementary OR #','OR Date','Status'].map(q));
    ciInvoices.forEach(inv => rows.push([
      inv.bir_invoice_number, inv.invoice_number,
      inv.bir_issued_at ? inv.bir_issued_at.split('T')[0] : '',
      inv.customer_name || 'Walk-in', inv.bir_customer_tin || '',
      fmt(inv.vatable_sales), fmt(inv.zero_rated_sales), fmt(inv.vat_exempt_sales),
      fmt(inv.vat_amount), fmt(inv.grand_total),
      inv.bir_or_number || '', inv.bir_or_issued_at ? inv.bir_or_issued_at.split('T')[0] : '',
      inv.status === 'voided' ? 'VOID' : 'ISSUED',
    ].map(q)));
    rows.push(['','','','','CI SUBTOTAL', fmt(ciVatable), fmt(ciZeroRated), fmt(ciExempt), fmt(ciVat), fmt(ciTotal),'','',''].map(q));
    rows.push(['']);
    // Grand Total
    rows.push([q('GRAND TOTAL (SI + CI)'), '', '', '', '',
      q(fmt((siVatable + ciVatable))), q(fmt((siZeroRated + ciZeroRated))),
      q(fmt((siExempt + ciExempt))), q(fmt((siVat + ciVat))), q(fmt((siTotal + ciTotal))),
    ]);
    const csv = rows.map(r => Array.isArray(r) ? r.join(',') : r).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `BIR-SLSP-${effectiveBranch}-${fromDate}-to-${toDate}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success('SLSP CSV exported');
  };

  const exportAgingCSV = () => {
    if (!agingList.length) { toast.error('No data to export'); return; }
    const headers = ['CI #', 'Internal Ref', 'Customer', 'TIN', 'CI Date', 'Original Amount', 'Amount Paid', 'Balance', 'Days Outstanding', 'Aging Bucket', 'OR Issued?'];
    const rows = agingList.map(r => [
      r.bir_invoice_number || '', r.invoice_number || '', r.customer_name || 'Walk-in', r.bir_customer_tin || '',
      r.bir_issued_at ? formatDate(r.bir_issued_at) : '',
      parseFloat(r.grand_total || 0).toFixed(2), parseFloat(r.amount_paid || 0).toFixed(2),
      r.computed_balance.toFixed(2), r.days_outstanding, r.aging_bucket,
      r.bir_or_number ? `Yes (${r.bir_or_number})` : 'No',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `BIR-AR-Aging-${effectiveBranch}-${today}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success('AR Aging exported');
  };

  const exportScpwdCSV = () => {
    if (!scpwdRows.length) { toast.error('No SC/PWD sales to export'); return; }
    const headers = ['Date', 'Invoice #', 'Type', 'Cardholder Name', 'OSCA/PWD ID', 'Gross Sales', 'VAT-Exempt Sales', 'VAT Removed', 'Discount', 'Net Amount', 'Cashier'];
    const rows = scpwdRows.map(r => [
      r.date, r.invoice_number, r.type, r.card_name, r.card_id,
      r.gross.toFixed(2), r.vat_exempt_sales.toFixed(2), r.vat_amount.toFixed(2),
      r.discount_amount.toFixed(2), r.net_amount.toFixed(2), r.cashier_name || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `BIR-SCPWD-Log-${effectiveBranch}-${fromDate}-to-${toDate}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success('SC/PWD logbook exported');
  };

  const paginatedInvoices = invoices.slice(page * LIMIT, (page + 1) * LIMIT);
  const totalPages = Math.ceil(invoices.length / LIMIT);
  const branchName = (branches || []).find(b => b.id === effectiveBranch)?.name || currentBranch?.name || '';
  const isBirActive = birStatus?.bir_activation_status === 'active';

  // ── BIR SLSP: Split invoices by document type ──────────────────────────────
  const siInvoices = invoices.filter(inv => inv.bir_document_label === 'SALES INVOICE');
  const ciInvoices = invoices.filter(inv => inv.bir_document_label === 'CHARGE INVOICE');
  const _sumField = (arr, field) => arr.filter(i => i.status !== 'voided').reduce((s, i) => s + parseFloat(i[field] || 0), 0);
  const siVatable = _sumField(siInvoices, 'vatable_sales');
  const siVat = _sumField(siInvoices, 'vat_amount');
  const siExempt = _sumField(siInvoices, 'vat_exempt_sales');
  const siZeroRated = _sumField(siInvoices, 'zero_rated_sales');
  const siTotal = _sumField(siInvoices, 'grand_total');
  const ciVatable = _sumField(ciInvoices, 'vatable_sales');
  const ciVat = _sumField(ciInvoices, 'vat_amount');
  const ciExempt = _sumField(ciInvoices, 'vat_exempt_sales');
  const ciZeroRated = _sumField(ciInvoices, 'zero_rated_sales');
  const ciTotal = _sumField(ciInvoices, 'grand_total');
  const ciPaidCount = ciInvoices.filter(i => i.status !== 'voided' && i.bir_or_number).length;
  const ciUnpaidCount = ciInvoices.filter(i => i.status !== 'voided' && !i.bir_or_number).length;
  const siActive = siInvoices.filter(i => i.status !== 'voided');
  const ciActive = ciInvoices.filter(i => i.status !== 'voided');
  const firstSiNumber = siActive[0]?.bir_invoice_number || '—';
  const lastSiNumber = siActive[siActive.length - 1]?.bir_invoice_number || '—';
  const firstCiNumber = ciActive[0]?.bir_invoice_number || '—';
  const lastCiNumber = ciActive[ciActive.length - 1]?.bir_invoice_number || '—';

  // ── Filter bar (shared across tabs) ────────────────────────────────────────
  const FilterBar = () => (
    <Card style={{ border: '1px solid var(--border-default)' }} className="print:hidden">
      <CardContent className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          {isAdmin && (branches || []).length > 1 && (
            <div>
              <p className="text-xs text-slate-500 mb-1">Branch</p>
              <select className="h-9 text-sm border rounded-md px-3 bg-white" style={{ borderColor: 'var(--border-default)' }}
                value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)} data-testid="bir-branch-filter">
                {(branches || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          {activeTab === 'taxable' && (
            <div>
              <p className="text-xs text-slate-500 mb-1">Quarter (2550Q)</p>
              <select className="h-9 text-sm border rounded-md px-3 bg-white" style={{ borderColor: 'var(--border-default)' }}
                value={quarterSel} onChange={e => applyQuarter(e.target.value)} data-testid="bir-quarter-select">
                <option value="">Custom range…</option>
                {quarterOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
          {activeTab !== 'ar_aging' && activeTab !== 'zreports' && (
            <>
              <div>
                <p className="text-xs text-slate-500 mb-1">From</p>
                <Input type="date" className="h-9 text-sm w-40" value={fromDate} onChange={e => { setFromDate(e.target.value); setQuarterSel(''); }} data-testid="bir-from-date" />
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">To</p>
                <Input type="date" className="h-9 text-sm w-40" value={toDate} onChange={e => { setToDate(e.target.value); setQuarterSel(''); }} data-testid="bir-to-date" />
              </div>
              {activeTab === 'taxable' && (
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer mb-0.5">
                  <input type="checkbox" checked={includeVoided} onChange={e => setIncludeVoided(e.target.checked)} />
                  Include Voided
                </label>
              )}
            </>
          )}
          <Button size="sm" className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-9" onClick={loadAll} disabled={loading} data-testid="bir-apply-filters-btn">
            {loading ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : null} Apply
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-5 animate-fadeIn print:p-0" data-testid="bir-compliance-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>
            <FileCheck size={22} className="text-[#1A4D2E]" />
            BIR Compliance
          </h1>
          <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            Official invoice ledger · {branchName}
            {isBirActive && <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] font-semibold">BIR Active</Badge>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isBirActive && isAdminOrManager && (
            <Button size="sm" className="h-9 bg-[#1A4D2E] hover:bg-[#14532d] text-white" onClick={() => setShowGenerateDialog(true)} data-testid="bir-generate-zreport-btn">
              <FilePlus size={13} className="mr-1.5" /> Generate BIR Z-Report
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} className="h-9" data-testid="bir-refresh-btn">
            <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {activeTab === 'taxable' && (
            <Button variant="outline" size="sm" onClick={exportTaxableCSV} disabled={!invoices.length} className="h-9" data-testid="bir-export-csv-btn">
              <Download size={13} className="mr-1.5" /> Export CSV
            </Button>
          )}
          {activeTab === 'ar_aging' && (
            <Button variant="outline" size="sm" onClick={exportAgingCSV} disabled={!agingList.length} className="h-9" data-testid="bir-export-aging-csv-btn">
              <Download size={13} className="mr-1.5" /> Export CSV
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => window.print()} className="h-9" data-testid="bir-print-btn">
            <Printer size={13} className="mr-1.5" /> Print
          </Button>
        </div>
      </div>

      {/* Not activated state */}
      {birStatus && !isBirActive && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertTriangle size={20} className="text-amber-600 shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">BIR Compliance Not Activated for This Branch</p>
              <p className="text-sm text-amber-700 mt-0.5">
                {birStatus.bir_activation_status === 'pending_review'
                  ? 'Your BIR activation is under review.'
                  : 'Activate BIR Compliance in Settings → Business Info → BIR Compliance.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex border-b print:hidden" style={{ borderColor: 'var(--border-default)' }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-[#1A4D2E] text-[#1A4D2E]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`bir-tab-${tab.id}`}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      <FilterBar />

      {/* ── TAB: Taxable Sales ─────────────────────────────────────────────────── */}
      {activeTab === 'taxable' && (
        <>
          {/* BIR EOPT Note */}
          <div className="flex gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
            <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-blue-800">BIR RR 7-2024 (EOPT) — Quarterly SLSP Format</p>
              <p className="text-xs text-blue-700 mt-0.5">
                <strong>SI</strong> and <strong>CI</strong> are principal taxable documents — output VAT is declared at issuance.&nbsp;
                <strong>OR</strong> is a supplementary proof-of-collection issued when a CI is paid — it is <em>not</em> a new taxable event.
                This report is structured for <strong>BIR Form 2550Q</strong> and the <strong>Summary List of Sales (SLSP)</strong>.
              </p>
            </div>
          </div>

          {/* Summary Row */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              <SummaryCard
                label="Sales Invoices (SI)"
                value={siActive.length}
                sub={`Cash · ${formatPHP(siTotal)}`}
                color="text-[#1A4D2E]"
              />
              <SummaryCard
                label="Charge Invoices (CI)"
                value={ciActive.length}
                sub={`${ciPaidCount} collected · ${ciUnpaidCount} unpaid`}
                color="text-amber-700"
              />
              <SummaryCard label="Vatable Sales" value={formatPHP(summary.vatable_sales)} />
              <SummaryCard label="VAT (12%)" value={formatPHP(summary.vat_amount)} color="text-blue-700" />
              <SummaryCard label="VAT-Exempt" value={formatPHP(summary.vat_exempt_sales)} />
              <SummaryCard label="Gross Sales" value={formatPHP(summary.gross_sales)} color="text-[#1A4D2E]" />
              <div className="rounded-xl border p-4 bg-white" style={{ borderColor: 'var(--border-default)' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Invoice Range</p>
                {siActive.length > 0 && <p className="text-[10px] font-mono text-slate-600"><span className="text-[9px] text-slate-400 uppercase mr-1">SI</span>{firstSiNumber} → {lastSiNumber}</p>}
                {ciActive.length > 0 && <p className="text-[10px] font-mono text-slate-600 mt-0.5"><span className="text-[9px] text-slate-400 uppercase mr-1">CI</span>{firstCiNumber} → {lastCiNumber}</p>}
                {!siActive.length && !ciActive.length && <p className="text-sm text-slate-400">No data</p>}
              </div>
            </div>
          )}

          {/* ── Section A: Sales Invoices (SI) ── */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-[#1A4D2E] text-white text-[11px] font-bold shrink-0">A</span>
              <h3 className="font-bold text-[15px] text-slate-800">Sales Invoices (SI)</h3>
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Principal — Cash / Digital</Badge>
              <span className="text-xs text-slate-400">VAT declared at issuance · Fully settled at POS · No follow-up document</span>
            </div>
            <Card style={{ border: '1px solid var(--border-default)' }}>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">SI #</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">OS Ref</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date Issued</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">TIN</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Vatable</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Exempt</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">VAT (12%)</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Gross Total</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cashier</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {siInvoices.map(inv => {
                        const isVoided = inv.status === 'voided';
                        return (
                          <TableRow key={inv.id} className={`cursor-pointer ${isVoided ? 'opacity-50 bg-red-50/30' : 'hover:bg-slate-50'}`}
                            onClick={() => setDetailInvoiceId(inv.id)} data-testid={`si-row-${inv.id}`}>
                            <TableCell><span className="font-mono text-xs font-bold text-[#1A4D2E]">{inv.bir_invoice_number}</span></TableCell>
                            <TableCell className="text-xs text-slate-500 font-mono">{inv.invoice_number}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{inv.bir_issued_at ? formatDate(inv.bir_issued_at) : '—'}</TableCell>
                            <TableCell className="text-sm max-w-[140px] truncate">{inv.customer_name || 'Walk-in'}</TableCell>
                            <TableCell className="text-[11px] font-mono text-slate-400">{inv.bir_customer_tin || '—'}</TableCell>
                            <TableCell className="text-right text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>{inv.vatable_sales ? formatPHP(inv.vatable_sales) : '—'}</TableCell>
                            <TableCell className="text-right text-xs text-slate-500" style={{ fontVariantNumeric: 'tabular-nums' }}>{inv.vat_exempt_sales ? formatPHP(inv.vat_exempt_sales) : '—'}</TableCell>
                            <TableCell className="text-right text-xs text-blue-600 font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{inv.vat_amount ? formatPHP(inv.vat_amount) : '—'}</TableCell>
                            <TableCell className="text-right text-sm font-bold text-slate-800" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(inv.grand_total)}</TableCell>
                            <TableCell className="text-xs text-slate-500">{inv.cashier_name || '—'}</TableCell>
                            <TableCell>{isVoided ? <Badge className="bg-red-100 text-red-700 text-[10px]">VOID</Badge> : <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">ISSUED</Badge>}</TableCell>
                          </TableRow>
                        );
                      })}
                      {!loading && !siInvoices.length && (
                        <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400 text-sm">{isBirActive ? 'No Sales Invoices for this period.' : 'BIR not active for this branch.'}</TableCell></TableRow>
                      )}
                      {loading && <TableRow><TableCell colSpan={11} className="text-center py-8"><RefreshCw size={16} className="animate-spin mx-auto text-slate-300" /></TableCell></TableRow>}
                      {siActive.length > 0 && (
                        <TableRow className="bg-emerald-50/60 border-t-2" style={{ borderColor: '#d1fae5' }}>
                          <TableCell colSpan={5} className="text-xs font-bold text-slate-600 text-right py-2">SI SUBTOTAL &nbsp;·&nbsp; {siActive.length} invoice{siActive.length !== 1 ? 's' : ''}</TableCell>
                          <TableCell className="text-right text-xs font-semibold py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(siVatable)}</TableCell>
                          <TableCell className="text-right text-xs font-semibold py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(siExempt)}</TableCell>
                          <TableCell className="text-right text-xs font-bold text-blue-700 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(siVat)}</TableCell>
                          <TableCell className="text-right text-sm font-bold text-[#1A4D2E] py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(siTotal)}</TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ── Section B: Charge Invoices (CI) ── */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-amber-600 text-white text-[11px] font-bold shrink-0">B</span>
              <h3 className="font-bold text-[15px] text-slate-800">Charge Invoices (CI)</h3>
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Principal — Credit / Charge</Badge>
              <span className="text-xs text-slate-400">OR issued as supplementary document on collection · Still taxable at CI issuance</span>
            </div>
            <Card style={{ border: '1px solid var(--border-default)' }}>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">CI #</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">OS Ref</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date Issued</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">TIN</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Vatable</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Exempt</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">VAT (12%)</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Gross Total</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Supplementary OR</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ciInvoices.map(inv => {
                        const isVoided = inv.status === 'voided';
                        const hasOr = !!inv.bir_or_number;
                        return (
                          <TableRow key={inv.id} className={`cursor-pointer ${isVoided ? 'opacity-50 bg-red-50/30' : 'hover:bg-slate-50'}`}
                            onClick={() => setDetailInvoiceId(inv.id)} data-testid={`ci-row-${inv.id}`}>
                            <TableCell><span className="font-mono text-xs font-bold text-amber-700">{inv.bir_invoice_number}</span></TableCell>
                            <TableCell className="text-xs text-slate-500 font-mono">{inv.invoice_number}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{inv.bir_issued_at ? formatDate(inv.bir_issued_at) : '—'}</TableCell>
                            <TableCell className="text-sm max-w-[140px] truncate">{inv.customer_name || 'Walk-in'}</TableCell>
                            <TableCell className="text-[11px] font-mono text-slate-400">{inv.bir_customer_tin || '—'}</TableCell>
                            <TableCell className="text-right text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>{inv.vatable_sales ? formatPHP(inv.vatable_sales) : '—'}</TableCell>
                            <TableCell className="text-right text-xs text-slate-500" style={{ fontVariantNumeric: 'tabular-nums' }}>{inv.vat_exempt_sales ? formatPHP(inv.vat_exempt_sales) : '—'}</TableCell>
                            <TableCell className="text-right text-xs text-blue-600 font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{inv.vat_amount ? formatPHP(inv.vat_amount) : '—'}</TableCell>
                            <TableCell className="text-right text-sm font-bold text-slate-800" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(inv.grand_total)}</TableCell>
                            <TableCell>
                              {hasOr ? (
                                <div className="flex flex-col gap-0">
                                  <span className="font-mono text-[11px] font-bold text-blue-700">{inv.bir_or_number}</span>
                                  {inv.bir_or_issued_at && <span className="text-[10px] text-slate-400">{formatDate(inv.bir_or_issued_at)}</span>}
                                </div>
                              ) : (
                                <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px]">Unpaid</Badge>
                              )}
                            </TableCell>
                            <TableCell>{isVoided ? <Badge className="bg-red-100 text-red-700 text-[10px]">VOID</Badge> : <Badge className="bg-amber-100 text-amber-700 text-[10px]">ISSUED</Badge>}</TableCell>
                          </TableRow>
                        );
                      })}
                      {!loading && !ciInvoices.length && (
                        <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400 text-sm">{isBirActive ? 'No Charge Invoices for this period.' : 'BIR not active for this branch.'}</TableCell></TableRow>
                      )}
                      {loading && <TableRow><TableCell colSpan={11} className="text-center py-8"><RefreshCw size={16} className="animate-spin mx-auto text-slate-300" /></TableCell></TableRow>}
                      {ciActive.length > 0 && (
                        <TableRow className="bg-amber-50/60 border-t-2" style={{ borderColor: '#fde68a' }}>
                          <TableCell colSpan={5} className="text-xs font-bold text-slate-600 text-right py-2">CI SUBTOTAL &nbsp;·&nbsp; {ciActive.length} invoice{ciActive.length !== 1 ? 's' : ''} · {ciPaidCount} collected · {ciUnpaidCount} unpaid</TableCell>
                          <TableCell className="text-right text-xs font-semibold py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(ciVatable)}</TableCell>
                          <TableCell className="text-right text-xs font-semibold py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(ciExempt)}</TableCell>
                          <TableCell className="text-right text-xs font-bold text-blue-700 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(ciVat)}</TableCell>
                          <TableCell className="text-right text-sm font-bold text-amber-700 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(ciTotal)}</TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ── Grand Total Block ── */}
          {summary && (siActive.length > 0 || ciActive.length > 0) && (
            <div className="flex justify-end">
              <div className="rounded-xl border bg-white p-5 min-w-[360px] space-y-2 shadow-sm" style={{ borderColor: 'var(--border-default)' }}>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">Grand Total — SI + CI (2550Q / SLSP)</p>
                <div className="flex justify-between text-sm"><span className="text-slate-600">Vatable Sales <span className="text-xs text-slate-400">(excl. VAT)</span></span><span className="font-semibold font-mono tabular-nums">{formatPHP(summary.vatable_sales)}</span></div>
                {(siZeroRated + ciZeroRated) > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-slate-600">Zero-Rated Sales</span><span className="font-semibold font-mono tabular-nums">{formatPHP(siZeroRated + ciZeroRated)}</span></div>
                )}
                <div className="flex justify-between text-sm"><span className="text-slate-600">VAT-Exempt Sales</span><span className="font-semibold font-mono tabular-nums">{formatPHP(summary.vat_exempt_sales)}</span></div>
                <div className="flex justify-between text-sm border-t pt-2" style={{ borderColor: 'var(--border-default)' }}>
                  <span className="text-blue-700 font-medium">Output VAT (12%)</span>
                  <span className="font-bold font-mono tabular-nums text-blue-700">{formatPHP(summary.vat_amount)}</span>
                </div>
                <div className="flex justify-between text-base font-bold border-t pt-2" style={{ borderColor: 'var(--border-default)' }}>
                  <span className="text-slate-800">Gross Sales <span className="text-xs font-normal text-slate-400">(inc. VAT)</span></span>
                  <span className="text-[#1A4D2E] font-mono tabular-nums text-lg">{formatPHP(summary.gross_sales)}</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── TAB: BIR AR Aging ─────────────────────────────────────────────────── */}
      {activeTab === 'ar_aging' && (
        <>
          {agingSummary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryCard label="Unpaid CIs" value={agingSummary.total_unpaid_cis} sub="Charge invoices with balance > 0" color="text-red-600" />
              <SummaryCard label="Total Outstanding" value={formatPHP(agingSummary.total_outstanding)} color="text-red-700" />
              <SummaryCard label="Original Amount" value={formatPHP(agingSummary.total_original_amount)} />
              <div className="rounded-xl border p-4 bg-white" style={{ borderColor: 'var(--border-default)' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">By Aging Bucket</p>
                {Object.entries(agingSummary.by_bucket || {}).map(([bucket, amount]) => (
                  <div key={bucket} className="flex justify-between text-xs mb-0.5">
                    <span className="text-slate-500">{bucket}</span>
                    <span className={`font-semibold ${amount > 0 ? 'text-red-600' : 'text-slate-400'}`}>{formatPHP(amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
            <TrendingDown size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-amber-800 font-semibold">BIR Bad Debt Documentation</p>
              <p className="text-xs text-amber-700 mt-0.5">This report lists only Charge Invoices (officially issued via BIR CI series) with outstanding balances. Use this for tax deductions when filing for bad debt losses due to customer defaults. This is separate from the internal AR at /payments.</p>
            </div>
          </div>

          <Card style={{ border: '1px solid var(--border-default)' }}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">CI #</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Internal Ref</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">CI Date</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Original</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Paid</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Balance</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Days</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Aging</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">OR Issued?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agingList.map(r => (
                      <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailInvoiceId(r.id)} data-testid={`aging-row-${r.id}`}>
                        <TableCell><span className="font-mono text-xs font-bold text-amber-700">{r.bir_invoice_number}</span></TableCell>
                        <TableCell className="text-xs text-slate-500 font-mono">{r.invoice_number}</TableCell>
                        <TableCell className="text-sm">
                          <div>{r.customer_name || 'Walk-in'}</div>
                          {r.bir_customer_tin && <div className="text-[10px] text-slate-400 font-mono">{r.bir_customer_tin}</div>}
                        </TableCell>
                        <TableCell className="text-xs">{r.bir_issued_at ? formatDate(r.bir_issued_at) : '—'}</TableCell>
                        <TableCell className="text-right text-xs font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(r.grand_total)}</TableCell>
                        <TableCell className="text-right text-xs text-emerald-600" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(r.amount_paid)}</TableCell>
                        <TableCell className="text-right text-sm font-bold text-red-600" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(r.computed_balance)}</TableCell>
                        <TableCell className="text-xs text-slate-500">{r.days_outstanding}d</TableCell>
                        <TableCell><AgingBadge bucket={r.aging_bucket} /></TableCell>
                        <TableCell>
                          {r.bir_or_number
                            ? <span className="text-[10px] font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{r.bir_or_number}</span>
                            : <span className="text-[10px] text-slate-400">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!agingList.length && !loading && (
                      <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-400">
                        {!isBirActive ? 'Activate BIR to track Charge Invoices.' : 'No unpaid Charge Invoices found.'}
                      </TableCell></TableRow>
                    )}
                    {loading && <TableRow><TableCell colSpan={10} className="text-center py-12"><RefreshCw size={18} className="animate-spin mx-auto text-slate-400" /></TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ── TAB: SC/PWD Logbook (RR 7-2010) ─────────────────────────────────── */}
      {activeTab === 'scpwd' && (
        <div className="space-y-3" data-testid="scpwd-log-section">
          <Card style={{ border: '1px solid var(--border-default)' }}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                    <Accessibility size={15} className="text-[#1A4D2E]" /> Senior Citizen / PWD Sales Logbook
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">RR 7-2010 — discounted sales with cardholder details, for {branchName}.</p>
                </div>
                <Button size="sm" variant="outline" onClick={exportScpwdCSV} disabled={!scpwdRows.length} data-testid="scpwd-export-csv">
                  <Download size={13} className="mr-1.5" /> Export CSV
                </Button>
              </div>

              {scpwdSummary && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                  {[
                    { label: 'Transactions', value: scpwdSummary.count, plain: true },
                    { label: 'Gross', value: formatPHP(scpwdSummary.gross) },
                    { label: 'VAT-Exempt', value: formatPHP(scpwdSummary.vat_exempt_sales) },
                    { label: 'Discount', value: formatPHP(scpwdSummary.discount_amount) },
                    { label: 'Net', value: formatPHP(scpwdSummary.net_amount) },
                  ].map(s => (
                    <div key={s.label} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">{s.label}</p>
                      <p className="text-sm font-bold text-[#1A4D2E]" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.plain ? s.value : s.value}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Cardholder</TableHead>
                      <TableHead>OSCA / PWD ID</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">VAT-Exempt</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scpwdLoading && (
                      <TableRow><TableCell colSpan={9} className="text-center py-12"><RefreshCw size={18} className="animate-spin mx-auto text-slate-400" /></TableCell></TableRow>
                    )}
                    {!scpwdLoading && scpwdRows.length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400 text-sm">No SC/PWD discounted sales in this period.</TableCell></TableRow>
                    )}
                    {!scpwdLoading && scpwdRows.map((r) => (
                      <TableRow key={r.id} data-testid={`scpwd-row-${r.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(r.date)}</TableCell>
                        <TableCell className="text-xs font-mono">{r.invoice_number}</TableCell>
                        <TableCell><Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">{r.type}</Badge></TableCell>
                        <TableCell className="text-xs">{r.card_name}</TableCell>
                        <TableCell className="text-xs font-mono">{r.card_id}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{formatPHP(r.gross)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{formatPHP(r.vat_exempt_sales)}</TableCell>
                        <TableCell className="text-right text-xs font-mono text-[#1A4D2E]">-{formatPHP(r.discount_amount)}</TableCell>
                        <TableCell className="text-right text-xs font-mono font-semibold">{formatPHP(r.net_amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── TAB: Z-Reports ──────────────────────────────────────────────────── */}
      {activeTab === 'zreports' && isBirActive && (
        <div className="space-y-2" data-testid="past-birz-section">
          <Card style={{ border: '1px solid var(--border-default)' }}>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">BIRZ Number</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Period</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice Range (SI/CI)</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Gross Sales</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">VAT</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Generated By</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {birzReports.map(rep => (
                    <TableRow key={rep.id} data-testid={`birz-row-${rep.id}`}>
                      <TableCell><span className="font-mono text-xs font-bold text-[#1A4D2E]">{rep.birz_number}</span></TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">{rep.period_label}</div>
                        <div className="text-slate-400">{rep.from_date} → {rep.to_date}</div>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-slate-600">
                        <div>{rep.or_range_first || '—'}</div>
                        <div className="text-slate-400">→ {rep.or_range_last || '—'}</div>
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold text-[#1A4D2E]" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(rep.gross_sales)}</TableCell>
                      <TableCell className="text-right text-xs text-blue-600" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(rep.vat_amount)}</TableCell>
                      <TableCell className="text-xs text-slate-500">
                        <div>{rep.generated_by_name}</div>
                        <div className="text-slate-400">{rep.generated_at ? formatDate(rep.generated_at) : ''}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleDownloadBirz(rep)} data-testid={`birz-download-${rep.id}`}>
                          <Download size={11} className="mr-1" /> PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!birzLoading && !birzReports.length && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400 text-sm">No BIR Z-Reports generated yet.</TableCell></TableRow>
                  )}
                  {birzLoading && <TableRow><TableCell colSpan={7} className="text-center py-8"><RefreshCw size={16} className="animate-spin mx-auto text-slate-400" /></TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── TAB: Product VAT Rules ──────────────────────────────────────────── */}
      {activeTab === 'vat_rules' && (
        <div className="space-y-6" data-testid="vat-rules-tab">

          {/* Explanation banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-blue-800">3-Layer VAT Rule Engine</p>
            <p className="text-xs text-blue-700 mt-1">
              <strong>Layer 1 — Category Rule (here):</strong> Set the default VAT class per product category (e.g., Seeds → VAT-Exempt).<br/>
              <strong>Layer 2 — Product Override:</strong> Individual products can deviate. Override is flagged and shown in the report below.<br/>
              <strong>Layer 3 — Sales Override:</strong> Cashiers can change VAT per transaction in /pos (requires manager PIN). Also saved globally.<br/>
              <span className="font-semibold text-blue-900">VAT status is GLOBAL — same across all branches.</span>
            </p>
          </div>

          {/* Section A — Category Rules */}
          <div className="space-y-3">
            <h3 className="font-bold text-[15px] text-slate-800 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#1A4D2E] text-white text-xs flex items-center justify-center font-bold">1</span>
              Category VAT Rules
            </h3>
            <Card style={{ border: '1px solid var(--border-default)' }}>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">VAT Rule</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Set By</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allCategories.map(cat => {
                      const rule = vatRules.find(r => r.category === (typeof cat === 'string' ? cat : cat.name));
                      const catName = typeof cat === 'string' ? cat : cat.name;
                      const currentClass = rule?.vat_class || 'vatable';
                      return (
                        <TableRow key={catName} data-testid={`vat-rule-row-${catName}`}>
                          <TableCell className="font-medium text-sm">{catName}</TableCell>
                          <TableCell>
                            <select
                              className="h-8 text-sm border rounded-md px-2 bg-white"
                              style={{ borderColor: 'var(--border-default)' }}
                              value={currentClass}
                              onChange={async (e) => {
                                const newClass = e.target.value;
                                try {
                                  await api.put('/bir/vat-category-rules', { category: catName, vat_class: newClass });
                                  toast.success(`Rule saved: ${catName} → ${newClass}`);
                                  loadVatRules();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                              }}
                              data-testid={`vat-rule-select-${catName}`}
                            >
                              <option value="vatable">Vatable (12%)</option>
                              <option value="vat_exempt">VAT-Exempt</option>
                              <option value="zero_rated">Zero-Rated (0%)</option>
                            </select>
                          </TableCell>
                          <TableCell className="text-xs text-slate-400">{rule?.set_by_name || '—'}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline" size="sm" className="h-7 text-xs"
                              disabled={applyingRule === catName || vatRulesLoading}
                              data-testid={`vat-rule-apply-${catName}`}
                              onClick={async () => {
                                if (!rule) { toast.error('Save a rule first'); return; }
                                setApplyingRule(catName);
                                try {
                                  const res = await api.post(`/bir/vat-category-rules/apply/${catName}`);
                                  setApplyResult(res.data);
                                  toast.success(`Applied to ${res.data.applied_count} products${res.data.skipped_count ? ` (${res.data.skipped_count} skipped)` : ''}`);
                                  loadVatRules();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                setApplyingRule('');
                              }}
                            >
                              {applyingRule === catName ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
                              Apply to Products
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!vatRulesLoading && !allCategories.length && (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No product categories found.</TableCell></TableRow>
                    )}
                    {vatRulesLoading && <TableRow><TableCell colSpan={4} className="text-center py-6"><RefreshCw size={16} className="animate-spin mx-auto text-slate-400" /></TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Apply result — shows skipped products */}
            {applyResult && applyResult.skipped_count > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <AlertTriangle size={14} /> {applyResult.skipped_count} product(s) skipped due to individual override or admin lock
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {(applyResult.skipped_products || []).map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-amber-700">
                      <span className="flex-1 truncate">{p.name}</span>
                      <Badge className={`text-[10px] ${p.reason === 'locked' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{p.reason}</Badge>
                      <span className="font-mono">{p.current_vat_class}</span>
                    </div>
                  ))}
                </div>
                <button className="text-xs text-amber-600 underline" onClick={() => setApplyResult(null)}>Dismiss</button>
              </div>
            )}
          </div>

          {/* Section B — Override Report */}
          <div className="space-y-3">
            <h3 className="font-bold text-[15px] text-slate-800 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center font-bold">2</span>
              Override Report
              {vatOverrideReport.total > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">{vatOverrideReport.total} products</Badge>}
            </h3>
            <p className="text-xs text-slate-500">Products whose individual VAT class differs from their category rule, or were overridden in /sales.</p>
            <Card style={{ border: '1px solid var(--border-default)' }}>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category Rule</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Actual VAT</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...vatOverrideReport.overrides, ...vatOverrideReport.locked].map(p => (
                      <TableRow key={p.id} data-testid={`override-row-${p.id}`}>
                        <TableCell className="text-sm font-medium">{p.name || p.product_name || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-500">{p.category || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">{p.category_rule || 'vatable'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${p.vat_class === 'vat_exempt' ? 'bg-slate-100 text-slate-600' : p.vat_class === 'zero_rated' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {p.vat_class || 'vatable'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {p.vat_class_locked
                            ? <Badge className="bg-red-100 text-red-700 text-[10px]">🔒 Locked</Badge>
                            : <Badge className="bg-amber-100 text-amber-700 text-[10px]">Override</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          {!p.vat_class_locked && (
                            <Button variant="outline" size="sm" className="h-7 text-xs" data-testid={`override-apply-rule-${p.id}`}
                              onClick={async () => {
                                const rule = vatRules.find(r => r.category === p.category);
                                if (!rule) { toast.error('No category rule set for this product\'s category'); return; }
                                try {
                                  await api.patch(`/products/${p.id}/vat-class`, { vat_class: rule.vat_class, from_branch_id: '', from_branch_name: 'Admin' });
                                  toast.success('Category rule applied');
                                  loadVatRules();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                              }}
                            >Apply Category Rule</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!vatOverrideReport.total && !vatRulesLoading && (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400 text-sm">No VAT overrides found — all products follow their category rules.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Section C — Conflict Queue (admin only) */}
          {isAdmin && vatOverrideReport.conflicts?.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-bold text-[15px] text-slate-800 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">3</span>
                Conflict Queue — Admin Resolution Required
                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">{vatOverrideReport.conflicts.length} conflict(s)</Badge>
              </h3>
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-xs text-red-700">Two or more branches have set conflicting VAT classes for these products. As admin, finalize the correct class — this will lock it and prevent further overrides from branches.</p>
              </div>
              <Card style={{ border: '1px solid var(--border-default)' }}>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conflicting Branches</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Finalize VAT Class</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vatOverrideReport.conflicts.map(p => (
                        <TableRow key={p.id} data-testid={`conflict-row-${p.id}`}>
                          <TableCell>
                            <div className="font-medium text-sm">{p.name || p.product_name}</div>
                            <div className="text-xs text-slate-400">{p.category}</div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-0.5">
                              {(p.vat_conflict_branches || []).map((b, i) => (
                                <div key={i} className="text-xs text-slate-600">{b.branch_name}: <strong>{b.vat_class}</strong></div>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {['vatable', 'vat_exempt', 'zero_rated'].map(vc => (
                                <Button key={vc} variant="outline" size="sm" className="h-7 text-xs"
                                  data-testid={`conflict-resolve-${p.id}-${vc}`}
                                  onClick={async () => {
                                    try {
                                      await api.post(`/bir/resolve-vat-conflict/${p.id}`, { vat_class: vc });
                                      toast.success(`${p.name || p.product_name} → ${vc} (locked)`);
                                      loadVatRules();
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                  }}
                                >{vc === 'vatable' ? 'VAT' : vc === 'vat_exempt' ? 'Exempt' : '0%'} — Lock</Button>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* BIR settings info footer */}
      {isBirActive && (
        <div className="text-xs text-slate-400 flex flex-wrap gap-4 pt-2 border-t print:hidden" style={{ borderColor: 'var(--border-default)' }}>
          <span><strong>TIN:</strong> {birStatus.tin || '—'}</span>
          <span><strong>ATP:</strong> {birStatus.atp_number || '—'}</span>
          <span><strong>MSN:</strong> {birStatus.machine_serial || '—'}</span>
          <span><strong>VAT:</strong> {VAT_TYPE_LABELS[birStatus.vat_type] || birStatus.vat_type || '—'}</span>
        </div>
      )}

      {/* Generate BIRZ Dialog */}
      <GenerateBIRZDialog open={showGenerateDialog} onClose={() => setShowGenerateDialog(false)} branchId={effectiveBranch} onGenerated={loadBirzReports} />

      {/* Invoice Detail Modal — for clickable rows */}
      <InvoiceDetailModal
        compact
        open={!!detailInvoiceId}
        onOpenChange={(o) => { if (!o) setDetailInvoiceId(null); }}
        invoiceId={detailInvoiceId}
      />
    </div>
  );
}
